import { User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface FirestoreUserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerIds: string[];
  status?: 'pending' | 'approved';
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

function userProfileRef(uid: string) {
  return doc(db, 'users', uid);
}

export async function upsertUserProfile(user: User): Promise<void> {
  const providerIds = user.providerData.map((provider) => provider.providerId);
  const ref = userProfileRef(user.uid);
  try {
    const snapshot = await getDoc(ref);
    // Detect new signups: account created within the last 10 minutes
    // getOrInitializeAccount may create the doc before we get here, so we
    // can't rely on snapshot.exists() alone to know if this is a first signup.
    const hasStatus = snapshot.exists() && snapshot.data()?.status !== undefined;
    const creationTime = user.metadata.creationTime
      ? new Date(user.metadata.creationTime).getTime()
      : 0;
    const isNewSignup = !hasStatus && Date.now() - creationTime < 10 * 60 * 1000;

    const baseProfile = {
      uid: user.uid,
      email: user.email ?? null,
      displayName: user.displayName ?? null,
      photoURL: user.photoURL ?? null,
      providerIds,
      updatedAt: serverTimestamp(),
      ...(isNewSignup ? { status: 'pending' as const } : {}),
    };

    if (snapshot.exists()) {
      await setDoc(ref, baseProfile, { merge: true });
    } else {
      await setDoc(
        ref,
        {
          ...baseProfile,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  } catch (err: any) {
    console.error('[RouteX][Firestore] upsertUserProfile:error', err?.code ?? err);
    throw err;
  }
}

export async function getUserProfile(uid: string): Promise<FirestoreUserProfile | null> {
  try {
    const snapshot = await getDoc(userProfileRef(uid));
    if (!snapshot.exists()) return null;
    return snapshot.data() as FirestoreUserProfile;
  } catch (err: any) {
    console.error('[RouteX][Firestore] getUserProfile:error', err?.code ?? err);
    throw err;
  }
}
